import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, GripVertical, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import {
  SPAN_CLASSES,
  WIDTH_LABELS,
  type LayoutWidget,
  type WidgetDef,
} from './widgets/widgetRegistry';

interface WidgetFrameProps {
  widget: LayoutWidget;
  def: WidgetDef;
  editing: boolean;
  onCycleWidth?: (id: string) => void;
  onRemove?: (id: string) => void;
  /** Phones: step the widget one slot (absent at either end of the list). */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

/**
 * One grid cell. In view mode it's just the widget in its col-span. In edit
 * mode it becomes sortable (dnd-kit) and grows a chrome overlay: drag grip
 * (listeners ONLY on the grip), a width-cycling button and a remove button.
 * The widget body gets pointer-events-none so links/charts don't eat clicks.
 */
export function WidgetFrame({
  widget,
  def,
  editing,
  onCycleWidth,
  onRemove,
  onMoveUp,
  onMoveDown,
}: WidgetFrameProps) {
  const { t } = useI18n();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.id,
    disabled: !editing,
  });

  const Widget = def.component;

  return (
    <div
      ref={setNodeRef}
      data-widget-id={widget.id}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative min-w-0 ${SPAN_CLASSES[widget.w]} ${isDragging ? 'z-10 opacity-40' : ''}`}
    >
      {/* Phones: the chrome is a row above the widget (overlaid, it covered
          the figures), with ↑ ↓ steps since dragging a long list with a thumb
          is fiddly, and no width button (every widget is full width there).
          The bar spans the card's width with the four buttons sharing it
          evenly, so it lines up with the widget instead of floating right.
          From sm it floats over the top-right corner as before. */}
      {editing && (
        <div className="z-20 mb-2 flex w-full items-center gap-1 rounded-lg border border-border bg-card p-1 shadow-card sm:absolute sm:right-2 sm:top-2 sm:mb-0 sm:w-auto">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!onMoveUp}
            className="flex flex-1 items-center justify-center rounded-md p-1 text-muted-foreground transition hover:bg-gray-200 hover:text-foreground disabled:opacity-30 sm:hidden"
            title={t('dashboard.edit.moveUp')}
            aria-label={t('dashboard.edit.moveUp')}
          >
            <ArrowUp className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!onMoveDown}
            className="flex flex-1 items-center justify-center rounded-md p-1 text-muted-foreground transition hover:bg-gray-200 hover:text-foreground disabled:opacity-30 sm:hidden"
            title={t('dashboard.edit.moveDown')}
            aria-label={t('dashboard.edit.moveDown')}
          >
            <ArrowDown className="h-4 w-4" aria-hidden />
          </button>
          {/* touch-none: without it a finger on the grip scrolled the page and
              the browser cancelled the pointer before the drag could start. */}
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="flex flex-1 cursor-grab touch-none items-center justify-center rounded-md p-1 text-muted-foreground transition hover:bg-gray-200 hover:text-foreground active:cursor-grabbing sm:flex-none"
            title={t('dashboard.edit.dragHint')}
            aria-label={t('dashboard.edit.dragHint')}
          >
            <GripVertical className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onCycleWidth?.(widget.id)}
            className="hidden rounded-md px-1.5 py-1 font-mono sm:inline-block text-xs text-muted-foreground transition hover:bg-gray-200 hover:text-foreground"
            title={t('dashboard.edit.width')}
            aria-label={t('dashboard.edit.width')}
          >
            {WIDTH_LABELS[widget.w]}
          </button>
          <button
            type="button"
            onClick={() => onRemove?.(widget.id)}
            className="flex flex-1 items-center justify-center rounded-md p-1 text-muted-foreground transition hover:bg-red-500/10 hover:text-red-400 sm:flex-none"
            title={t('dashboard.edit.remove')}
            aria-label={t('dashboard.edit.remove')}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
      <div className={editing ? 'pointer-events-none h-full select-none' : 'h-full'}>
        <Widget />
      </div>
    </div>
  );
}
